// src/pages/TalleresCrear/index.jsx
import React, { useEffect, useState } from "react";
import { Steps, Form, Button, Space, Card } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, SaveOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";

import { useNotify } from "../../../../../providers/NotificationProvider";

// Services
import Recintos from "../../../../../service/Recintos";
import Talleres from "../../../../../service/Talleres";
import Horarios from "../../../../../service/Horarios";

// Components
import Paso0InfoBasica from "./components/Paso0InfoBasica";
import Paso1Fechas from "./components/Paso1Fechas";
import Paso2Modalidades from "./components/Paso2Modalidades";
import Paso3Resumen from "./components/Paso3Resumen";
import Paso4Horarios from "./components/Paso4Horarios";
import Paso5Confirmacion from "./components/Paso5Confirmacion";
import Paso6Aprobacion from "./components/Paso6Aprobacion";

// Utils
import { STEPS, PASO, ULTIMO_PASO_VISIBLE, AYUDA_PASO } from "./constants";

dayjs.locale("es");

export default function TalleresCrear() {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [recintos, setRecintos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tallerId, setTallerId] = useState(null);
  const [horariosCreados, setHorariosCreados] = useState([]);
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();

  const notify = useNotify();

  useEffect(() => {
    loadRecintos();
  }, []);

  /**
   * Volver arriba al cambiar de paso.
   *
   * El scroll se quedaba donde estaba, así que tras pulsar "Siguiente" en un paso
   * largo —Info básica ocupa casi mil líneas— el paso siguiente aparecía empezado
   * por la mitad, y el título y los primeros campos quedaban fuera de pantalla.
   *
   * Va en un efecto sobre `currentStep` y no dentro de handleNext para cubrir
   * también "Anterior" y los saltos automáticos posteriores a crear el taller.
   */
  useEffect(() => {
    const prefiereMenosMovimiento =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: prefiereMenosMovimiento ? 'auto' : 'smooth' });
  }, [currentStep]);

  const loadRecintos = async () => {
    try {
      const response = await Recintos.List({ limit: 0 });
      const recintosOptions = response.data.map(r => ({
        value: r._id,
        label: r.nombre
      }));

      recintosOptions.sort((a, b) => a.label.localeCompare(b.label));
      setRecintos(recintosOptions);
    } catch (error) {
      console.error('Error cargando recintos:', error);
      notify.error('Error al cargar recintos');
    }
  };

  const handleAutocompletar = () => {
    const now = dayjs();
    const randomRecinto = recintos.length > 0 ? recintos[0].value : null;

    const datosDemo = {
      nombre: `Taller Demo ${now.format('HH:mm:ss')}`,
      descripcion: 'Descripción de prueba para desarrollo',
      recinto: randomRecinto,
      tipoActividad: 'Deportivo',
      tipoDuracionSuscripcion: 'mes_calendario',
      nivelIntensidad: 'moderada', // 🆕 NUEVO
      edadMin: 18,
      edadMax: 65,
      // 🆕 Fechas con hora
      inscripcionInicio: now.add(1, 'hour'),
      inscripcionFin: now.add(1, 'day').hour(23).minute(45),
      fechasActividades: [now.add(2, 'days'), now.add(32, 'days')],
      esGratuito: false,
      liberarCuposPorBaja: false,
      soloTarjetaVecino: false,
      requiereMatricula: false,
      tieneIVA: false,
      accesoPiscina: false,
      avisoVencimiento: false,
      tipoTaller: 'libre',
      usuariosAutorizados: [],
      modalidades: {
        mensual: {
          activo: true,
          valorParticular: 15000,
          valorVecino: 12000,
          valorAdultoMayor: 10000,
          valorFuncionarios: 13000,
          matricula: 5000
        }
      }
    };

    form.setFieldsValue(datosDemo);
    setFormData(prev => ({ ...prev, ...datosDemo }));
    setTimeout(() => { form.validateFields().catch(() => { }); }, 100);
    notify.success('✅ Formulario autocompletado con datos de prueba');
  };

  const saveCurrentStepData = () => {
    const currentValues = form.getFieldsValue();
    setFormData(prev => ({ ...prev, ...currentValues }));
  };

  const handleNext = async () => {
    try {
      const fieldsToValidate = getFieldsForStep(currentStep);
      await form.validateFields(fieldsToValidate);

      if (currentStep === PASO.FECHAS_Y_PAGOS) {
        const values = form.getFieldsValue();
        const esGratuito = values.esGratuito;
        const modalidades = values.modalidades || {};

        if (!esGratuito) {
          const algunaModalidadActiva = Object.values(modalidades).some(modalidad => {
            return modalidad.activo === true &&
              (modalidad.valorParticular > 0 || modalidad.valorVecino > 0 || modalidad.valorAdultoMayor > 0 || modalidad.valorFuncionarios > 0 || modalidad.matricula > 0);
          });
          if (!algunaModalidadActiva) {
            notify.error('Debes seleccionar al menos una modalidad con valores cuando el taller no es gratuito');
            return;
          }
        }
      }
      saveCurrentStepData();
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Error de validación:', error);
      notify.error('Por favor completa todos los campos requeridos');
    }
  };

  const handlePrev = () => {
    saveCurrentStepData();
    setCurrentStep(currentStep - 1);
  };

  /**
   * Campos que valida cada paso antes de permitir avanzar.
   *
   * Ojo: `form.validateFields([])` NO valida nada — rc-field-form trata el array
   * como una lista explícita de campos. Los pasos 1 y 2 devolvían `[]`, así que
   * los validadores definidos en Paso1Fechas (fechasActividades, inscripcionInicio
   * e inscripcionFin) pintaban el error inline pero no bloqueaban "Siguiente":
   * se podía avanzar con fechas incoherentes o sin ventana de inscripción.
   *
   * Revisión, Horarios y las pantallas de resultado no tienen campos propios,
   * por eso devuelven `[]` a propósito.
   */
  const getFieldsForStep = (step) => {
    switch (step) {
      case PASO.TALLER:
        return ['nombre', 'descripcion', 'recinto', 'tipoActividad', 'tipoDuracionSuscripcion', 'nivelIntensidad']; // 🆕 añadido nivelIntensidad
      case PASO.FECHAS_Y_PAGOS:
        // Fechas y pagos comparten paso: se validan juntos. La regla de negocio
        // de las modalidades se comprueba aparte en handleNext, porque depende
        // de esGratuito y no se puede expresar como `rules` de un solo campo.
        return [
          'fechasActividades', 'inscripcionInicio', 'inscripcionFin',
          'esGratuito', 'liberarCuposPorBaja',
        ];
      default:
        return [];
    }
  };

  const handleCrearTaller = async () => {
    try {
      setLoading(true);
      saveCurrentStepData();

      const allValues = { ...formData, ...form.getFieldsValue() };
      
      // 🔍 Debug
      console.log('📝 Valores del formulario:', allValues);
      if (allValues.inscripcionInicio) {
        console.log('🕐 inscripcionInicio (local):', dayjs(allValues.inscripcionInicio).format('DD/MM/YYYY HH:mm'));
        console.log('🕐 inscripcionInicio (ISO):', dayjs(allValues.inscripcionInicio).toISOString());
      }
      if (allValues.inscripcionFin) {
        console.log('🕐 inscripcionFin (local):', dayjs(allValues.inscripcionFin).format('DD/MM/YYYY HH:mm'));
        console.log('🕐 inscripcionFin (ISO):', dayjs(allValues.inscripcionFin).toISOString());
      }
      
      const tallerData = {
        nombre: allValues.nombre,
        descripcion: allValues.descripcion,
        recinto: allValues.recinto,
        tipoActividad: allValues.tipoActividad,
        tipoDuracionSuscripcion: allValues.tipoDuracionSuscripcion,
        nivelIntensidad: allValues.nivelIntensidad || 'moderada', // 🆕 NUEVO
        esGratuito: allValues.esGratuito || false,
        liberarCuposPorBaja: allValues.liberarCuposPorBaja || false,
        soloTarjetaVecino: allValues.soloTarjetaVecino || false,
        requiereMatricula: allValues.requiereMatricula || false,
        tieneIVA: allValues.tieneIVA || false,
        accesoPiscina: allValues.accesoPiscina || false,
        avisoVencimiento: allValues.avisoVencimiento || false,
        tipoTaller: allValues.tipoTaller || 'libre',
        usuariosAutorizados: allValues.usuariosAutorizados || []
      };

      if (allValues.edadMin) tallerData.edadMin = allValues.edadMin;
      if (allValues.edadMax) tallerData.edadMax = allValues.edadMax;

      // 🆕 Fechas de inscripción CON HORA (campos separados)
      if (allValues.inscripcionInicio) {
        tallerData.inscripcionInicio = dayjs(allValues.inscripcionInicio).toISOString();
        console.log('📤 inscripcionInicio ISO:', tallerData.inscripcionInicio);
      }
      
      if (allValues.inscripcionFin) {
        tallerData.inscripcionFin = dayjs(allValues.inscripcionFin).toISOString();
        console.log('📤 inscripcionFin ISO:', tallerData.inscripcionFin);
      }

      // Fechas de actividades (sin hora, solo día completo)
      if (allValues.fechasActividades && allValues.fechasActividades[0] && allValues.fechasActividades[1]) {
        tallerData.actividadesInicio = dayjs(allValues.fechasActividades[0]).startOf('day').toISOString();
        tallerData.actividadesFin = dayjs(allValues.fechasActividades[1]).endOf('day').toISOString();
      }

      // Modalidades de pago
      if (!allValues.esGratuito && allValues.modalidades) {
        const modalidadPagoArray = [];
        Object.entries(allValues.modalidades).forEach(([plan, config]) => {
          if (config?.activo) {
            modalidadPagoArray.push({
              plan,
              valorParticular: config.valorParticular || 0,
              valorVecino: config.valorVecino || null,
              valorNoVecino: config.valorParticular || null,
              valorAdultoMayor: config.valorAdultoMayor || null,
              valorFuncionarios: config.valorFuncionarios || null,
              matricula: config.matricula || 0
            });
          }
        });
        tallerData.modalidadPago = modalidadPagoArray;
      }

      console.log('📤 Payload final:', tallerData);

      const response = await Talleres.Post(tallerData);

      setTallerId(response.taller?._id || response.cambioPendiente?._id);

      // El destino lo decide la RESPUESTA, no el rol de quien envía: el backend
      // devuelve `cambioPendiente` cuando los cambios de esa persona pasan por la
      // cola de revisión.
      //
      // Antes se preguntaba `isJefe_recinto`, así que un rol creado desde el panel
      // con "requiere aprobación" marcado veía "Taller creado exitosamente" y era
      // enviado a crear horarios de un taller que todavía no existía.
      if (response.cambioPendiente) {
        notify.info('El taller fue enviado a aprobación. Un administrador o coordinador debe revisarlo.');
        setCurrentStep(PASO.APROBACION);
      } else {
        notify.success('Taller creado exitosamente');
        setCurrentStep(PASO.HORARIOS);
      }
    } catch (error) {
      console.error('❌ Error creando taller:', error);
      
      if (error.code === 'MATRICULA_NOT_CONFIGURED') {
        notify.error('El recinto seleccionado no tiene configuración de matrícula. Configure la matrícula primero.');
      } else {
        notify.error(error.message || error.error || 'Error al crear el taller');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCrearHorarios = async (horariosData) => {
    if (!tallerId) {
      notify.error('No se ha creado el taller');
      return;
    }

    if (!horariosData || horariosData.length === 0) {
      notify.info('Continuando sin horarios. Podrás agregarlos más tarde.');
      setCurrentStep(PASO.CONFIRMACION);
      return;
    }

    try {
      setLoading(true);
      const promises = horariosData.map(horario => {
        const horarioData = {
          taller: tallerId,
          nombre: horario.nombre,
          sku: horario.sku,
          cupos: horario.cuposIlimitados ? 0 : horario.cuposDisponibles,
          cuposIlimitados: horario.cuposIlimitados || false,
          bloques: horario.bloques
        };
        return Horarios.Post(horarioData);
      });

      await Promise.all(promises);
      notify.success(`${horariosData.length} horario(s) creado(s) exitosamente`);
      setHorariosCreados(horariosData);
      setCurrentStep(PASO.CONFIRMACION);

    } catch (error) {
      console.error('❌ Error creando horarios:', error);
      notify.error(error.response?.data?.message || error.message || 'Error al crear horarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipHorarios = () => {
    notify.info('Podrás agregar horarios más tarde');
    setCurrentStep(PASO.CONFIRMACION);
  };

  const renderStepContent = () => {
    const allFormValues = { ...formData, ...form.getFieldsValue() };

    switch (currentStep) {
      case PASO.TALLER:
        return <Paso0InfoBasica recintos={recintos} form={form} />;
      case PASO.FECHAS_Y_PAGOS:
        // Dos pantallas de 3 y 2 campos que antes eran pasos separados. Van
        // juntas porque responden a la misma pregunta: cuándo se dicta y cuánto
        // cuesta. Los <Form.Item> viven en el mismo <Form>, así que fusionarlas
        // no cambia el estado ni la validación, solo deja de partir el recorrido.
        return (
          <>
            <Paso1Fechas form={form} />
            <Paso2Modalidades form={form} />
          </>
        );
      case PASO.REVISION:
        return <Paso3Resumen formValues={allFormValues} recintos={recintos} />;
      case PASO.HORARIOS:
        return (
          <Paso4Horarios
            tallerId={tallerId}
            onHorariosChange={setHorariosCreados}
            loading={loading}
          />
        );
      case PASO.CONFIRMACION:
        return (
          <Paso5Confirmacion
            tallerId={tallerId}
            nombreTaller={allFormValues.nombre}
            cantidadHorarios={horariosCreados.length}
          />
        );
      case PASO.APROBACION:
        return (
          <Paso6Aprobacion
            nombreTaller={allFormValues.nombre}
            onFinish={() => navigate('/admin/talleres/lista')}
          />
        );
      default:
        return null;
    }
  };

  const renderFooterButtons = () => {
    // Confirmación y Aprobación traen su propia acción de cierre.
    if (currentStep > ULTIMO_PASO_VISIBLE) return null;

    return (
      <Space size="middle" className="w-full justify-between">
        <Button
          size="large"
          onClick={() => navigate('/admin/talleres/lista')}
          icon={<ArrowLeftOutlined />}
        >
          Cancelar
        </Button>
        <Space>
          {/* Tras crear el taller ya no se puede volver atrás: en Horarios el
              registro existe en el backend. */}
          {currentStep > PASO.TALLER && currentStep < PASO.HORARIOS && (
            <Button size="large" onClick={handlePrev}>
              Anterior
            </Button>
          )}
          {currentStep < PASO.REVISION && (
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              icon={<ArrowRightOutlined />}
            >
              Siguiente
            </Button>
          )}
          {currentStep === PASO.REVISION && (
            <Button
              type="primary"
              size="large"
              onClick={handleCrearTaller}
              loading={loading}
              icon={<SaveOutlined />}
            >
              Crear taller
            </Button>
          )}
          {currentStep === PASO.HORARIOS && (
            <>
              <Button size="large" onClick={handleSkipHorarios}>
                Saltar este paso
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={() => handleCrearHorarios(horariosCreados)}
                loading={loading}
                disabled={!tallerId}
              >
                {horariosCreados.length > 0
                  ? `Finalizar con ${horariosCreados.length} horario(s)`
                  : 'Finalizar sin horarios'}
              </Button>
            </>
          )}
        </Space>
      </Space>
    );
  };

  const botonesPie = renderFooterButtons();

  return (
    // El AdminLayout ya pinta el fondo del panel; antes esta pantalla montaba
    // encima su propio `min-h-screen bg-gray-50`, un gris distinto al del resto.
    <div className="py-2">
      <div className="max-w-5xl mx-auto px-4">

        {/* Cabecera de página, con el mismo patrón que el resto del panel.
            Esta pantalla no tenía <h1>: empezaba directamente en el stepper, así
            que ni el lector de pantalla ni el usuario sabían dónde estaban. */}
        <div className="admin-page-header">
          <div>
            <h1 className="admin-page-title">Crear taller</h1>
            {currentStep <= ULTIMO_PASO_VISIBLE && (
              <p className="admin-page-subtitle">{AYUDA_PASO[currentStep]}</p>
            )}
          </div>
          {/* Atajo de desarrollo. Antes era un botón `block` de tamaño `large`
              sobre todo el ancho: lo primero que veía cualquiera al abrir la
              pantalla era una herramienta interna. */}
          {process.env.NODE_ENV === 'development' && currentStep < PASO.REVISION && (
            <Button
              type="dashed"
              size="small"
              icon={<ThunderboltOutlined />}
              onClick={handleAutocompletar}
              disabled={recintos.length === 0}
            >
              Autocompletar
            </Button>
          )}
        </div>

        <Card>
          {/* En Confirmación y Aprobación el taller ya está creado: mostrar el
              recorrido ahí solo confunde, porque ya no hay nada que recorrer. */}
          {currentStep <= ULTIMO_PASO_VISIBLE && (
            <Steps current={currentStep} items={STEPS} className="mb-8" />
          )}
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              esGratuito: false,
              liberarCuposPorBaja: false,
              tipoDuracionSuscripcion: 'mes_corrido',
              nivelIntensidad: 'moderada', // 🆕 NUEVO
              soloTarjetaVecino: false,
              requiereMatricula: false,
              tieneIVA: false,
              accesoPiscina: false,
              avisoVencimiento: false,
              tipoTaller: 'libre',
              usuariosAutorizados: []
            }}
          >
            {renderStepContent()}
          </Form>
        </Card>

        {/* Barra de navegación fija al pie.
            En pasos largos —Info básica ocupa casi mil líneas— "Siguiente"
            quedaba al final del scroll: había que recorrer todo el formulario
            para avanzar, incluso sin haber tocado los campos de abajo. */}
        {botonesPie && (
          <div className="wizard-footer">{botonesPie}</div>
        )}
      </div>
    </div>
  );
}
